import type { ReactNode } from "react";

import { TraceMetricsRow } from "@/components/llm-trace";
import { Card, CardHead } from "@/components/ui";
import type { AdminSessionMessage } from "@/types";
import { compactTime } from "@/utils/format";
import { sessionTypeLabel } from "@/utils/sessionType";
import type { NormalizedTrace } from "@/utils/trace/model";
import type { TraceEnvironment } from "./conversationsSearch";
import { messageFeedback } from "./messageFeedback";

/**
 * Who asked. The answer's own `trigger_message_id` when it resolves on the
 * loaded page, otherwise the nearest earlier user message — in a group chat
 * the session has many members, and "the user" is whoever sent the question.
 */
function askerOf(
  message: AdminSessionMessage,
  messages: readonly AdminSessionMessage[],
): string | null {
  if (message.trigger_message_id) {
    const trigger = messages.find((m) => m.chat_message_id === message.trigger_message_id);
    if (trigger?.sender_email) return trigger.sender_email;
  }
  const index = messages.indexOf(message);
  for (let i = index - 1; i >= 0; i -= 1) {
    if (messages[i].role === "user" && messages[i].sender_email) return messages[i].sender_email;
  }
  return null;
}

/**
 * The selected answer, identified — and the run summary beside it.
 *
 * The badges sit here rather than in the Trace card for the same reason Test
 * Result puts them by the score: they describe *this answer*, and reading them
 * should not require scrolling past the waterfall.
 */
export function MessageInspector({
  sessionId,
  environment,
  evaluationRuntime = false,
  sessionType,
  messageId,
  message,
  messages,
  position,
  trace,
  traceLoading,
  orphan,
}: {
  sessionId: string;
  environment: TraceEnvironment;
  evaluationRuntime?: boolean;
  sessionType: string;
  messageId: string;
  /** Null when the linked answer is off the loaded page. */
  message: AdminSessionMessage | null;
  messages: readonly AdminSessionMessage[];
  /** `[index, count]` among assistant messages, 1-based. */
  position: [number, number] | null;
  trace?: NormalizedTrace;
  traceLoading: boolean;
  orphan: boolean;
}) {
  const hint = position && position[1] > 1 ? `assistant ${position[0]} / ${position[1]}` : "식별자";

  const feedback = message ? messageFeedback(message) : null;
  const asker = message ? askerOf(message, messages) : null;

  return (
    <Card className="min-w-0">
      <CardHead title="메시지" hint={hint} />
      <div className="grid gap-3 p-4">
        {/* A linked answer can be older than the first page. Its trace still
            opens below; only the fields read off the message are missing. */}
        {orphan && (
          <p className="rounded-lg border border-border bg-surface-subtle px-3 py-2 text-xs text-muted-foreground">
            링크된 메시지가 최근 100건 안에 없어 대화 본문 없이 트레이스만 표시합니다.
          </p>
        )}
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-2">
          <Row label="session_id">{sessionId}</Row>
          <Row label="assistant_message_id">{messageId}</Row>
          {message && <Row label="시각">{compactTime(message.created_at)}</Row>}
          <Row label="환경">{evaluationRuntime ? "SAMS 보존 평가" : environment}</Row>
          {message && <Row label="사용자">{asker ?? "—"}</Row>}
          {sessionType && <Row label="세션 유형">{sessionTypeLabel(sessionType)}</Row>}
          {feedback && (
            <Row label="피드백">
              {feedback.down.length === 0 && feedback.up.length === 0 ? (
                "없음"
              ) : (
                <span className="grid gap-1">
                  {feedback.down.map((entry) => (
                    <span key={`down:${entry.email}`}>
                      👎 {entry.email}
                      {entry.comment && (
                        <span className="text-muted-foreground"> · {entry.comment}</span>
                      )}
                    </span>
                  ))}
                  {feedback.up.map((entry) => (
                    <span key={`up:${entry.email}`}>👍 {entry.email}</span>
                  ))}
                </span>
              )}
            </Row>
          )}
        </dl>
      </div>
      {trace ? (
        <TraceMetricsRow metrics={trace.metrics} className="mt-0 mb-0 border-t border-border px-4 py-3" />
      ) : (
        traceLoading && (
          <p className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
            실행 요약 불러오는 중…
          </p>
        )
      )}
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="pt-px text-[11px] whitespace-nowrap text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-[12px] break-all">{children}</dd>
    </>
  );
}
